import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Button} from 'react-bootstrap';

import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {showLicenseModal} from '../../stores/actions/licenceStateAction';
import {LicenseStatus, ViewMode} from '../../constants/dataEnum';
import {hideLoading, showLoading} from '../../stores/actions/appStateAction';
import {isUserOperator} from '../../stores/business/userManagementBusiness';
import {getInstrumentStatus} from '../../stores/business/instrumentBusiness';
import {showModalConnectionFailed} from '../../stores/business/commonModalBusiness';
import withIntl from '../../lib/withIntl';

import './DemoOnlyFloatingMessage.scss';

const mapStateToProps = (state) => ({
  licenseStatus: state.licenseState.status,
  viewMode: state.appState.viewMode,
});

const mapDispatchToProps = (dispatch) => ({
  licenseStateActionShowLicenseModal: bindActionCreators(showLicenseModal, dispatch),
  onGetInstrumentStatus: bindActionCreators(getInstrumentStatus, dispatch),
  onShowModalConnectionFailed: bindActionCreators(showModalConnectionFailed, dispatch),
  onShowLoading: bindActionCreators(showLoading, dispatch),
  onHideLoading: bindActionCreators(hideLoading, dispatch),
  userManagementBusinessIsOperator: bindActionCreators(isUserOperator, dispatch),
});

function DemoOnlyFloatingMessage({
  intl,
  licenseStatus,
  viewMode,
  licenseStateActionShowLicenseModal,
  onGetInstrumentStatus,
  onShowModalConnectionFailed,
  onShowLoading,
  onHideLoading,
  userManagementBusinessIsOperator,
}) {
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);
  const [isCollapse, setIsCollapse] = useState(false);
  const [isShow, setIsShow] = useState(false);

  const demoOnlyRef = useRef(null);

  function dragElement(elmnt) {
    let pos1 = 0;
    let pos2 = 0;
    let pos3 = 0;
    let pos4 = 0;

    function elementDrag(ev) {
      // eslint-disable-next-line no-param-reassign
      ev = ev || window.event;
      ev.preventDefault();
      // calculate the new cursor position:
      pos1 = pos3 - ev.clientX;
      pos2 = pos4 - ev.clientY;
      pos3 = ev.clientX;
      pos4 = ev.clientY;

      const newTopPos = elmnt.offsetTop - pos2;
      const newLeftPos = elmnt.offsetLeft - pos1;

      if (
        newTopPos < 0 ||
        newLeftPos < 0 ||
        newTopPos + elmnt.offsetHeight > window.innerHeight ||
        newLeftPos + elmnt.offsetWidth > window.innerWidth
      ) {
        return;
      }

      // set the element's new position:
      elmnt.style.top = ''.concat(newTopPos, 'px');
      elmnt.style.left = ''.concat(newLeftPos, 'px');
    }

    function closeDragElement() {
      /* stop moving when mouse button is released: */
      document.onmouseup = null;
      document.onmousemove = null;
    }

    function dragMouseDown(ev) {
      console.log('dragMouseDown');
      // eslint-disable-next-line no-param-reassign
      ev = ev || window.event;
      ev.preventDefault();
      // get the mouse cursor position at startup:
      pos3 = ev.clientX;
      pos4 = ev.clientY;
      document.onmouseup = closeDragElement;
      // call a function whenever the cursor moves:
      document.onmousemove = elementDrag;
    }

    if (elmnt.querySelector('.handlerD')) {
      // if present, the header is where you move the DIV from
      elmnt.querySelector('.handlerD').onmousedown = dragMouseDown;
    } else {
      // otherwise, move the DIV from anywhere inside the DIV
      elmnt.onmousedown = dragMouseDown;
    }
  }

  useEffect(() => {
    setIsShow(
      viewMode !== ViewMode.LOGIN &&
        viewMode !== ViewMode.LOGIN_CONFIRMATION &&
        licenseStatus !== LicenseStatus.TRIAL &&
        licenseStatus !== LicenseStatus.PERMANENT &&
        licenseStatus !== LicenseStatus.TEMPORARY &&
        licenseStatus !== LicenseStatus.PERMANENT_EXPIRED &&
        isInstrumentOperational,
    );

    return () => setIsCollapse(false);
  }, [licenseStatus, viewMode, isInstrumentOperational]);

  useEffect(() => {
    if (demoOnlyRef.current && isShow) {
      const element = demoOnlyRef.current;
      const elementPos = {
        left: element.offsetLeft,
        top: element.offsetTop,
      };
      const {innerHeight} = window;
      element.style.left = `${elementPos.left + 23}px`;
      element.style.top = `${elementPos.top + innerHeight - 200}px`;

      dragElement(demoOnlyRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow]);

  useEffect(() => {
    if (demoOnlyRef.current) {
      const element = demoOnlyRef.current;
      const elementPos = {
        left: element.offsetLeft,
        top: element.offsetTop,
        right: element.offsetLeft + element.offsetWidth,
      };
      if (isCollapse) {
        element.style.top = `${elementPos.top + 91}px`;
      } else {
        const newTop = elementPos.top - 91;
        element.style.top = `${newTop > 0 ? newTop : elementPos.top}px`;
        if (elementPos.right > window.innerWidth) {
          element.style.left = `${window.innerWidth - 408}px`;
        }
      }
    }
  }, [isCollapse]);

  const activateButtonHandler = async () => {
    onShowLoading(intl.formatMessage({id: 'instrument.loading.checkConnection'}));
    const response = await onGetInstrumentStatus();
    onHideLoading();
    if (!response) {
      onShowModalConnectionFailed();
    } else {
      licenseStateActionShowLicenseModal();
    }
  };

  return (
    <div className="bootstrap-iso">
      {isShow && (
        <div ref={demoOnlyRef} className={`demo-only ${isCollapse ? 'collapse' : ''}`}>
          <div className="demo-only__handler handlerD">
            <span className="icon-dragged" />
          </div>
          <div className="demo-only__logo">
            <span className="icon-fast-logo" />
          </div>
          <div className="demo-only__content">
            <div className="demo-only__content__header">
              <span>{intl.formatMessage({id: 'licenseing.demoOnly.floatingMessage.title'})}</span>
              {isCollapse && (
                <span
                  className="demo-only__content__collapse-button icon-chevron-up"
                  onClick={() => setIsCollapse(false)}
                  role="presentation"
                />
              )}
            </div>
            {!isCollapse && (
              <>
                <div className="demo-only__content__body">
                  {intl.formatMessage({id: 'licenseing.demoOnly.floatingMessage.text'})}
                </div>
                <div className="demo-only__content__button">
                  <Button
                    className="button-primary"
                    onClick={activateButtonHandler}
                    disabled={userManagementBusinessIsOperator()}
                  >
                    {intl.formatMessage({id: 'licenseing.demoOnly.floatingMessage.firstButton'})}
                  </Button>
                  <Button className="button-tertiary" onClick={() => setIsCollapse(true)}>
                    {intl.formatMessage({id: 'licenseing.demoOnly.floatingMessage.secondButton'})}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

DemoOnlyFloatingMessage.propTypes = {
  intl: PropTypes.any.isRequired,
  licenseStatus: PropTypes.string.isRequired,
  viewMode: PropTypes.string.isRequired,
  licenseStateActionShowLicenseModal: PropTypes.func.isRequired,
  onGetInstrumentStatus: PropTypes.func.isRequired,
  onShowModalConnectionFailed: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  userManagementBusinessIsOperator: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(DemoOnlyFloatingMessage));
